import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import { VIEW_ONLY_ICON } from './KindMenu';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './OtherSourcesEmpty.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * A sibling of `DirectoryEmpty`, not a fifth branch inside it.
 *
 * `DirectoryEmpty` is keyed on `DirectoryKind` through four `Record<DirectoryKind, …>` tables — the
 * CTA's label, the CTA's add item, the "what this kind is for" lead and the new-deck body — plus
 * `KIND_ICON` and `KIND_LABEL`. `other` is not a kind and has an entry for none of them: it has no
 * add item (you cannot make an other source, you connect an app), no "there are 8 artifacts
 * elsewhere" story (this list is not a cut of the deck, so nothing is elsewhere), and no plural
 * noun that reads — "No other sources yet" is a sentence about the deck when the real message is
 * about an account setting one screen away.
 *
 * Widening the four tables would mean five records each carrying one entry that is a special case
 * in every one, and an `onCreate` that means "make a thing" for four kinds and "leave for Settings"
 * for the fifth. Two small components that each say one thing beat one that says two.
 *
 * What IS shared is the shape — same glyph, same headline, same one route out — and it is shared by
 * being drawn the same way, in this component's own stylesheet, not by sharing a class.
 */
export interface OtherSourcesEmptyProps {
  /**
   * Apps this account has already connected, by display name (`connectedAppNames`).
   *
   * It changes the sentence, and that is the point: "Connect Figma, Linear, GitHub or Notion" shown
   * to somebody who connected Figma last week is not false, but it reads as if the product forgot —
   * and the honesty rule this whole view is built on cuts both ways. Empty (the default, and the
   * common case) gets the invitation; non-empty gets the truth about why the list is still bare.
   */
  connected?: string[];
  /**
   * Leave for the integrations screen.
   *
   * Omit it and NO button is drawn. This is a design-system component and does not navigate — the
   * route shell does, exactly as `SettingsRouteShell` does for the settings nav — and a pill that
   * announces "Connect an app" and answers nothing is the kind of control this pass exists to
   * remove. No handler, no button, and the sentence above it still says what to do.
   */
  onConnect?: () => void;
  theme?: HeaderTheme;
  className?: string;
}

/** `['Figma', 'Linear', 'GitHub']` → `'Figma, Linear and GitHub'`. */
function listAnd(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The Other sources empty state: the integrations mark, what is missing, and the way to fix it. */
export function OtherSourcesEmpty({
  connected = [],
  onConnect,
  theme: themeProp,
  className,
}: OtherSourcesEmptyProps) {
  const theme = useV15Theme(themeProp);

  // Nothing connected is the resting state and gets the invitation. Something connected gets the
  // fact, because the reader has already done the thing the invitation would ask for and the real
  // answer to "why is this empty" is that Deck cannot list that app's contents yet.
  const headline = connected.length === 0 ? 'No other sources' : 'Nothing pulled in yet';
  const body =
    connected.length === 0
      ? 'Connect Figma, Linear, GitHub or Notion to pull sources in.'
      : `${listAnd(connected)} ${connected.length === 1 ? 'is' : 'are'} connected. Nothing has been pulled in from ${connected.length === 1 ? 'it' : 'them'} yet.`;

  return (
    <div className={cn(themes[theme], styles.empty, className)}>
      {/* The switcher's own mark for this view, held as far back as the kind boards hold theirs —
          it labels the block, it is not an illustration. */}
      <span className={styles.glyph} aria-hidden>
        <DeckIcon name={VIEW_ONLY_ICON.other} size={22} />
      </span>
      <p className={styles.headline}>{headline}</p>
      <p className={styles.body}>{body}</p>
      {onConnect ? (
        <button type="button" className={styles.cta} onClick={onConnect}>
          {/* Not a `+`. Every other CTA in this panel makes something here in the deck; this one
              leaves for another screen, and the arrow out is the one glyph that says so before the
              click rather than after it. */}
          <DeckIcon name="arrow-right-up" size={14} />
          <span className={styles.ctaLabel}>
            {connected.length === 0 ? 'Connect an app' : 'Manage apps'}
          </span>
        </button>
      ) : null}
    </div>
  );
}
